import * as Sentry from "@sentry/vue";
import https from "https";
import { isAuthenticated } from "~/assets/utils/auth";

// Protected routes that require authentication
// Using regex patterns for precise matching
const PROTECTED_ROUTES: RegExp[] = [
  /^\/api\/UserPackages$/,
  /^\/api\/Address$/,
  /^\/api\/Order\/(dates|get|detail|recent)/,
  /^\/api\/User$/,
  /^\/api\/paypal\/token/,
  /^\/api\/Paypal\/hasPaymentToken/,
  /^\/api\/Authentication\/extend-session$/,
  /^\/api\/SavedSearch/,
  /^\/api\/Refund\/RefundOrder/,
  /^\/api\/Stripe\/get-customer-discount$/,
  /^\/api\/Stripe\/get-payment-methods$/,
  /^\/api\/Stripe\/detach-payment-method/,
  /^\/api\/Stripe\/get-customer-balance$/,
];

const isProtectedRoute = (url: string): boolean => {
  // Extract path from URL (remove base URL and query params)
  const path = url.replace(/^https?:\/\/[^/]+/, "").split("?")[0];
  return PROTECTED_ROUTES.some((pattern) => pattern.test(path));
};

export default function ({ app, store, $axios }: any) {
  // Set up the HTTPS agent to ignore certificate errors
  $axios.defaults.httpsAgent = new https.Agent({ rejectUnauthorized: false });

  // Request interceptor: Block protected routes if not authenticated
  $axios.onRequest((config: any) => {
    const url = config.url || "";
    if (isProtectedRoute(url) && !isAuthenticated()) {
      console.warn(`[Auth Guard] Blocked request to protected route: ${url}`);
      // Cancel the request by returning a rejected promise
      return Promise.reject({
        __CANCELED__: true,
        message: "Request blocked: User not authenticated",
      });
    }
    return config;
  });

  // Error handling for Axios requests
  $axios.onError((error: any) => {
    // Handle canceled requests from auth guard
    if (error?.__CANCELED__) {
      return Promise.resolve(null);
    }
    if (error.response) {
      // Get the HTTP status code
      const status = error.response.status;
      // Handle 401 Unauthorized errors
      if (status === 401) {
        // Always clear local tokens and session data on 401 to prevent further unauthorized calls
        if (typeof window !== "undefined") {
          localStorage.removeItem("jwt");
          localStorage.removeItem("session");
        }
        $axios.setToken(false); // Remove Bearer token from axios

        if (app.$auth?.user) {
          store.dispatch("user/logoutUser");
          window.location.reload();
        } else {
          // Clear auth storage even if $auth.user doesn't exist
          app.$auth?.$storage?.removeUniversal("user");
          app.$auth?.$storage?.removeUniversal("loggedIn");
        }
        // Prevent the error from being sent to Sentry
        return Promise.resolve();
      }
      // Expected client-side errors (4xx) are part of normal app flow
      // (validation, not found, conflicts, etc.). Record them as breadcrumbs
      // so they show up as context if a real exception is later captured,
      // but do not send them as standalone Sentry events.
      if (status >= 400 && status < 500) {
        Sentry.addBreadcrumb({
          category: "http",
          level: "warning",
          message: `HTTP ${status} ${error.config?.method ?? ""} ${error.config?.url ?? ""}`,
          data: {
            url: error.config?.url,
            method: error.config?.method,
            status,
          },
        });
        return Promise.reject(error);
      }
    }
    // Server errors (5xx) and network/transport errors (no response) are genuine
    // failures: capture them as Sentry events with full context.
    Sentry.captureException(error, {
      tags: { source: "axiosInterceptor" },
      extra: {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status
      }
    });
    // Return a rejected promise to handle the error properly in the calling code
    return Promise.reject(error);
  });
}
